/* global google */
import { Loader } from "@googlemaps/js-api-loader";
import { Card, Empty, Skeleton } from "antd";
import isEqual from "lodash/isEqual";
import React from "react";
class GoogleMap extends React.Component {
    constructor(props) {
        super(props);
        this.mapRef = React.createRef();
        this.markers = [];
        this.infoWindow = null;
        this.map = null;
        this.geocoder = null;
        this.geocodeCache = new Map();
        this.AdvancedMarkerElement = null;
        this.PinElement = null;
        this.originalConsoleError = null;
        this.Map = null;
        this.loader = null;
        this.mapReadyTimeout = null;

        this.state = {
            scriptLoaded: false,
            mapReady: false,
            loading: false,
            hasError: false,
            errorType: null,
            errorMessage: "",
            currentLocation: null,
        };
    }

    componentDidMount() {
        this.loadGoogleMapsScript();
    }

    componentDidUpdate(prevProps) {
        // Handle data changes
        const addressChanged = this.props.address !== prevProps.address;
        const markersChanged = !isEqual(this.props.markers, prevProps.markers);
        const preResolvedChanged = !isEqual(this.props.preResolvedLocation, prevProps.preResolvedLocation);

        if (!this.state.hasError && this.state.mapReady && (addressChanged || markersChanged || preResolvedChanged)) {
            this.debouncedHandleDataChange();
        }
    }

    componentWillUnmount() {
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
        }
        this.cleanup();
    }
    //clean up resources
    cleanup = () => {
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError;
            this.originalConsoleError = null;
        }
        if (this.markers.length > 0) {
            this.markers.forEach((marker) => {
                marker.map = null;
            });
        }
        this.markers = [];
        if (this.map) {
            this.map = null;
        }
        if (this.infoWindow) {
            this.infoWindow = null;
        }
        if (this.mapReadyTimeout) {
            clearTimeout(this.mapReadyTimeout);
        }
    };
    // Reset and reload the map script
    retryLoadMap = () => {
        this.cleanup();
        this.setState({ hasError: false, errorType: null, errorMessage: "" });
        this.loadGoogleMapsScript();
    };
    // error handling
    handleError = (type, message) => {
        this.cleanup();
        this.setState({
            hasError: true,
            errorType: type,
            errorMessage: message,
            loading: false,
            scriptLoaded: false,
            mapReady: false,
        });
        this.props.onError(type, message);
    };

    debouncedHandleDataChange = () => {
        if (this.updateTimer) {
            clearTimeout(this.updateTimer);
        }
        this.setState({ loading: true });
        this.updateTimer = setTimeout(() => {
            this.handleDataChange();
        }, 800);
    };

    loadGoogleMapsScript = async () => {
        const API_KEY = this.props.keyreq;

        if (!this.originalConsoleError) {
            this.originalConsoleError = console.error;
            console.error = (...args) => {
                this.originalConsoleError.apply(console, args);
                const errorMessage = args.join(" ");
                if (errorMessage.includes("InvalidKeyMapError")) {
                    this.handleError("INVALID_API_KEY", "Invalid Google Maps API key. Please check your API key configuration in Google Cloud Console.");
                } else if (errorMessage.includes("RefererNotAllowedMapError")) {
                    this.handleError(
                        "REFERER_NOT_ALLOWED",
                        " Pretectum.net is not authorized to use this API key. Please add `pretectum.net` to the list of allowed referrers in Google cloud console.",
                    );
                } else if (errorMessage.includes("RequestDeniedMapError")) {
                    this.handleError("REQUEST_DENIED", "Google Maps API request was denied. Please check your API key permissions and billing status.");
                } else if (errorMessage.includes("OverQuotaMapError")) {
                    this.handleError("QUOTA_EXCEEDED", "Google Maps API quota has been exceeded. Please check your usage limits.");
                } else if (errorMessage.includes("NotFoundMapError")) {
                    this.handleError("MAP_NOT_FOUND", "Google Maps API could not load the requested map.");
                }
            };
        }

        window.gm_authFailure = () => {
            if (!this.state.hasError) {
                console.error("Google Maps authentication failed. Please check your API key and billing status.");
                // this.handleError('AUTH_ERROR', 'Google Maps authentication failed. Please check your API key and billing status.');
            }
        };

        this.loader = new Loader({
            apiKey: API_KEY,
            version: "weekly",
        });

        try {
            await this.loader.load();
            const { Map } = await google.maps.importLibrary("maps");
            const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
            const { Geocoder } = await google.maps.importLibrary("geocoding");
            const { InfoWindow } = await google.maps.importLibrary("maps");
            this.Map = Map;
            this.AdvancedMarkerElement = AdvancedMarkerElement;
            this.PinElement = PinElement;
            this.geocoder = new Geocoder();
            this.infoWindow = new InfoWindow();

            this.setState({ scriptLoaded: true }, () => {
                this.initMap();
                this.handleDataChange();
            });
        } catch (error) {
            if (!this.state.hasError) {
                this.handleError("SCRIPT_LOAD_ERROR", "Failed to load the Google Maps script. Check network connection.");
            }
        }
    };

    handleDataChange = () => {
        if (!this.state.scriptLoaded) {
            this.setState({ loading: false });
            return;
        }

        const { address, markers, preResolvedLocation } = this.props;

        if (preResolvedLocation) {
            // Bypass geocoding entirely and use metadata from database
            this.setState({ currentLocation: preResolvedLocation, loading: false });
            this.updateMapWithLocation(preResolvedLocation);
            // DO NOT call onSuccess here, otherwise it triggers an infinite metadata save loop
        } else if (address) {
            this.handleAddressGeocode(address);
        } else if (markers && markers.length > 0) {
            this.handleMarkerData(markers);
        } else {
            this.setState({ currentLocation: null, loading: false });
        }
    };

    handleAddressGeocode = (address) => {
        if (String(address) === "####" || String(address).includes("####")) {
            this.setState({ loading: false });
            this.handleError("MASKED_DATA", "Location data is masked. Please unmask the data to view the location on the map.");
            return;
        }
        if (!address || typeof address !== "string" || address.trim().length < 3) {
            this.setState({ loading: false });
            this.handleError("INVALID_ADDRESS", "Invalid address format");
            return;
        }

        // Check cache first
        const cacheKey = `address_${address.toLowerCase().trim()}`;
        if (this.geocodeCache.has(cacheKey)) {
            const cachedResult = this.geocodeCache.get(cacheKey);
            if (cachedResult.error) {
                this.setState({ loading: false });
                this.handleError(cachedResult.error.type, cachedResult.error.message);
            } else {
                this.setState({ currentLocation: cachedResult, loading: false });
                this.updateMapWithLocation(cachedResult);
                this.props.onSuccess && this.props.onSuccess(cachedResult);
            }
            return;
        }
        if (!this.geocoder) {
            this.setState({ loading: false });
            this.handleError("GEOCODER_NOT_READY", "Geocoder is not initialized. Please try again.");
            return;
        }
        this.geocoder.geocode({ address }, (results, status) => {
            this.handleGeocodeResult(results, status, cacheKey, address);
        });
    };

    handleMarkerData = (markers) => {
        const { latitude, longitude } = markers[0].position;

        if (String(latitude) === "####" || String(longitude) === "####" || String(latitude).includes("####") || String(longitude).includes("####")) {
            this.setState({ loading: false });
            this.handleError("MASKED_DATA", "Location coordinates are masked. Please unmask the data to view the location on the map.");
            return;
        }
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            this.setState({ loading: false });
            this.handleError("INVALID_COORDINATES", "Invalid coordinates provided");
            return;
        }

        const location = { latitude: lat, longitude: lng, isExplicitMarker: true };
        this.setState({ currentLocation: location, loading: false });
        this.updateMapWithLocation(location);
        this.props.onSuccess && this.props.onSuccess(location);
    };

    handleGeocodeResult = (results, status, cacheKey, originalData) => {
        if (status === "OK" && results && results.length > 0) {
            const location = {
                latitude: results[0].geometry.location.lat(),
                longitude: results[0].geometry.location.lng(),
                address: results[0].formatted_address,
                locationType: results[0].geometry.location_type,
                partialMatch: results[0].partial_match || false,
            };
            this.geocodeCache.set(cacheKey, location);
            this.setState({ currentLocation: location, loading: false });
            this.updateMapWithLocation(location);
            this.props.onSuccess && this.props.onSuccess(location);
        } else {
            const errorInfo = this.getGeocodeErrorInfo(status);
            this.geocodeCache.set(cacheKey, { error: errorInfo });
            this.setState({ loading: false });
            this.handleError(errorInfo.type, errorInfo.message);
        }
    };

    getGeocodeErrorInfo = (status) => {
        switch (status) {
            case "ZERO_RESULTS":
                return { type: "ZERO_RESULTS", message: "No location results found for this address" };
            case "OVER_QUERY_LIMIT":
                return { type: "OVER_QUERY_LIMIT", message: "Google Maps quota exceeded" };
            case "REQUEST_DENIED":
                return { type: "REQUEST_DENIED", message: "Geocoding request was denied" };
            case "INVALID_REQUEST":
                return { type: "INVALID_REQUEST", message: "Invalid geocoding request" };
            case "UNKNOWN_ERROR":
                return { type: "UNKNOWN_ERROR", message: "Unknown error occurred during geocoding" };
            default:
                return { type: "GEOCODING_ERROR", message: `Geocoding failed with status: ${status}` };
        }
    };

    initMap = () => {
        const mapRef = this.mapRef.current;
        if (!mapRef) {
            console.error("Map container element not found");
            return;
        }

        const mapOptions = {
            center: { lat: -34.397, lng: 150.644 },
            zoom: 8,
            mapId: "DEMO_MAP_ID",
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true,
        };

        this.map = new this.Map(mapRef, mapOptions);
        this.mapReadyTimeout = setTimeout(() => {
            this.handleError(
                "MAP_LOAD_TIMEOUT",
                "Map failed to render tiles. This may indicate an API key,referrer, billing, or network issue.Please check Google Cloud Console and your network connection.",
            );
        }, 10000);
        // Listen for the 'tilesloaded' event. This confirms the map is working.
        this.map.addListener("tilesloaded", () => {
            // If tiles load successfully, clear the timeout and set the map as ready.
            clearTimeout(this.mapReadyTimeout);
            if (!this.state.mapReady) {
                this.setState({ mapReady: true });
            }
        });
    };

    updateMapWithLocation = (location) => {
        if (!this.map || !location) return;
        this.markers.forEach((marker) => {
            marker.map = null;
        });
        this.markers = [];

        const position = { lat: location.latitude, lng: location.longitude };
        this.map.setCenter(position);
        this.map.setZoom(15);
        const marker = new this.AdvancedMarkerElement({
            position: position,
            map: this.map,
            title: location.address || `${location.latitude}, ${location.longitude}`,
            // animation: window.google.maps.Animation.DROP
        });
        marker.addListener("click", () => {
            this.infoWindow.setContent(`
        <div>
          <h4>Location Information</h4>
          <p><strong>Address:</strong> ${location.address || "N/A"}</p>
          <p><strong>Coordinates:</strong> ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}</p>
        </div>
      `);
            this.infoWindow.open({
                anchor: marker,
                map: this.map,
            });
        });

        this.markers.push(marker);
    };
    render() {
        if (this.state.hasError) {
            return null;
        }
        const { address, markers } = this.props;
        const { scriptLoaded, loading, currentLocation } = this.state;

        return (
            <div>
                <Card className="rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex min-w-0 items-center">
                            <span className="mr-2 mt-0 flex-shrink-0 text-lg" aria-label="location-pin">
                                📍
                            </span>
                            <div className="min-w-0">
                                {currentLocation && !loading ? (
                                    <span
                                        className="block truncate text-sm font-light text-gray-700"
                                        title={currentLocation.address || `${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}`}
                                    >
                                        {currentLocation.address || `${currentLocation.latitude.toFixed(4)}, ${currentLocation.longitude.toFixed(4)}`}
                                    </span>
                                ) : loading ? (
                                    <span className="text-xs italic text-blue-600">Loading location...</span>
                                ) : (
                                    <span className="text-xs italic text-gray-500">No location data available</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="relative min-h-[410px] w-full rounded-lg border border-blue-400 bg-gray-100 p-1">
                        <div ref={this.mapRef} className="h-[400px] w-full"></div>

                        {!scriptLoaded && (
                            <div className="bg-opacity-99 absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-200">
                                <div className="text-center">
                                    <Skeleton active />
                                    <p className="mt-4 text-gray-600">Loading Google Maps...</p>
                                </div>
                            </div>
                        )}

                        {scriptLoaded && loading && (
                            <div className="bg-opacity-99 absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white">
                                <div className="text-center">
                                    <Skeleton active />
                                    <p className="mt-4 font-medium italic text-gray-600">Geocoding address...</p>
                                </div>
                            </div>
                        )}
                        {scriptLoaded && !loading && !address && (!markers || markers.length === 0) && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-100">
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No address or coordinates data available" />
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        );
    }
}

export default GoogleMap;
